import catalogJson from "../../data/catalog.json";
import landingsJson from "../../data/landings.json";
import redirectsJson from "../../data/redirects.json";

export const PARTNER_ID = "32566";
export const YBERA_HOST = "www.ybera.com";
export const PIX_DISCOUNT = 0.05;

export const FEATURED_SLUGS = [
  "escova-progressiva-300g-fashion-gold",
  "escova-progressiva-fashion-gold-500g",
  "combo-escova-progressiva-fashion-gold-300g-protect-poo-300g",
  "combo-escova-progressiva-fashion-gold-500g-protect-poo-500g",
  "kit-manutencao-pos-progressiva-300ml-250g-fashion-gold",
  "kit-manutencao-pos-progressiva-500ml-500g-fashion-gold",
] as const;

export type CheckoutKind = "affiliate" | "missing-official-url";

export type CatalogProduct = {
  wpId?: number;
  sku: string;
  name: string;
  slug: string;
  sourceSlug?: string;
  path: string;
  type: "external" | "simple" | string;
  checkout: CheckoutKind;
  officialUrl: string | null;
  partnerId: string;
  price: number | null;
  regularPrice: number | null;
  salePrice: number | null;
  currency: "BRL" | string;
  inStock: boolean;
  onSale: boolean;
  shortDescription: string;
  description: string;
  categories: string[];
  categoryNames: string[];
  image: string | null;
  images: string[];
  duplicateSlugs?: string[];
  needsReview?: boolean;
  hidden?: boolean;
  hiddenReason?: "linha-fora-de-linha" | "url-404-pendente" | "duplicate-slug";
};

export type SeoLanding = {
  wpId: number | null;
  slug: string;
  path: string;
  title: string | null;
  keepAsLanding: boolean;
  modified: string | null;
  excerpt: string;
};

export type CatalogRedirect = {
  from: string;
  to: string;
  reason: "duplicate-slug" | "canonicalize-primary" | string;
  wpId?: number;
};

export const products = catalogJson as CatalogProduct[];
export const listedProducts = products.filter((product) => !product.hidden);
export const landings = landingsJson as SeoLanding[];
export const redirects = redirectsJson as CatalogRedirect[];

export function getProductBySlug(slug: string): CatalogProduct | undefined {
  const product = products.find((item) => item.slug === slug);
  if (!product || product.hidden) return undefined;
  return product;
}

function fold(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

export function searchCatalog(query: string, limit = 8): CatalogProduct[] {
  const term = fold(query.trim());
  if (term.length < 2) return [];

  return listedProducts
    .map((product) => {
      const name = fold(product.name);
      const slug = fold(product.slug.replace(/-/g, " "));
      const blurb = fold(product.shortDescription);
      let rank = 9;
      if (name.startsWith(term) || name.includes(` ${term}`)) rank = 0;
      else if (name.includes(term) || slug.includes(term)) rank = 1;
      else if (blurb.includes(term)) rank = 2;
      return { product, rank };
    })
    .filter((entry) => entry.rank < 9)
    .sort((a, b) => a.rank - b.rank || a.product.name.localeCompare(b.product.name, "pt-BR"))
    .slice(0, limit)
    .map((entry) => entry.product);
}

export function productsNeedingReview(): CatalogProduct[] {
  return listedProducts.filter((product) => product.needsReview);
}

export function toYberaCheckoutUrl(raw: string, partnerId = PARTNER_ID): string {
  const url = new URL(raw);
  if (url.hostname.replace(/^www\./, "") === "b2cstore.com.br") {
    url.hostname = YBERA_HOST;
  }
  url.searchParams.set("parceiro", partnerId);
  return url.toString();
}

export function affiliateUrl(product: CatalogProduct): string | null {
  if (!product.officialUrl) return null;
  return toYberaCheckoutUrl(product.officialUrl, product.partnerId || PARTNER_ID);
}

export function pixAmount(price: number): number {
  return Math.round(price * (1 - PIX_DISCOUNT) * 100) / 100;
}

export function installmentAmount(price: number, times = 6): number {
  return Math.round((price / times) * 100) / 100;
}

export function variantLabel(product: CatalogProduct): string {
  const match = product.name.match(/(\d+\s?(?:g|ml))/i);
  if (match) return match[1].replace(/\s+/g, "").toLowerCase();
  if (/kit|combo/i.test(product.name)) return "Kit";
  return "Único";
}

export function featuredProducts(): CatalogProduct[] {
  return FEATURED_SLUGS.map((slug) => getProductBySlug(slug)).filter(
    (product): product is CatalogProduct => Boolean(product),
  );
}

const BROAD_CATEGORIES = new Set(["ybera-paris", "fashion-gold"]);
const RELATED_STOP = new Set([
  "ybera",
  "paris",
  "fashion",
  "gold",
  "combo",
  "kit",
  "com",
  "para",
  "linha",
  "oficial",
]);

function relatedNameKey(value: string): string {
  return fold(value).replace(/[^a-z0-9]+/g, " ").trim();
}

function relatedTokens(product: CatalogProduct): Set<string> {
  const raw = fold(`${product.name} ${product.slug} ${product.categories.join(" ")}`);
  return new Set(
    raw
      .split(/[^a-z0-9]+/)
      .filter((token) => token.length >= 4 && !RELATED_STOP.has(token) && !/^\d+$/.test(token)),
  );
}

function hasProductCheckout(product: CatalogProduct): boolean {
  return Boolean(product.officialUrl?.includes("/produto/"));
}

export function relatedProducts(product: CatalogProduct, limit = 4): CatalogProduct[] {
  const currentName = relatedNameKey(product.name);
  const currentUrl = product.officialUrl ?? "";
  const currentTokens = relatedTokens(product);
  const specificCats = product.categories.filter((category) => !BROAD_CATEGORIES.has(category));

  const ranked = listedProducts
    .filter((item) => {
      if (item.slug === product.slug) return false;
      if (!item.image) return false;
      if (!hasProductCheckout(item)) return false;
      if (relatedNameKey(item.name) === currentName) return false;
      if (currentUrl && item.officialUrl === currentUrl) return false;
      return true;
    })
    .map((item) => {
      const sharedSpecific = specificCats.filter((category) => item.categories.includes(category)).length;
      const itemTokens = relatedTokens(item);
      let tokenHits = 0;
      for (const token of currentTokens) {
        if (itemTokens.has(token)) tokenHits += 1;
      }
      const featured = FEATURED_SLUGS.includes(item.slug as (typeof FEATURED_SLUGS)[number]) ? 3 : 0;
      const score = sharedSpecific * 12 + tokenHits * 4 + featured + (item.inStock ? 1 : 0);
      return { item, score };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || a.item.name.localeCompare(b.item.name, "pt-BR"));

  const picked: CatalogProduct[] = [];
  const seenNames = new Set<string>([currentName]);
  const seenUrls = new Set<string>(currentUrl ? [currentUrl] : []);

  const take = (item: CatalogProduct) => {
    const nameKey = relatedNameKey(item.name);
    const url = item.officialUrl ?? item.slug;
    if (seenNames.has(nameKey) || seenUrls.has(url)) return;
    seenNames.add(nameKey);
    seenUrls.add(url);
    picked.push(item);
  };

  for (const { item } of ranked) {
    take(item);
    if (picked.length >= limit) return picked;
  }

  for (const slug of FEATURED_SLUGS) {
    const item = getProductBySlug(slug);
    if (!item || item.slug === product.slug || !item.image || !hasProductCheckout(item)) continue;
    take(item);
    if (picked.length >= limit) break;
  }

  return picked;
}

export function formatBRL(value: number | null | undefined): string {
  if (value == null) return "";
  return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

export function lowestFeaturedPrice(): number {
  const prices = featuredProducts()
    .map((product) => (product.price != null ? pixAmount(product.price) : null))
    .filter((price): price is number => typeof price === "number");
  return prices.length ? Math.min(...prices) : 129.9;
}
